import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { tap } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { NzModalService } from "ng-zorro-antd/modal";
import { Router } from "@angular/router";
import { StoreService } from "@root/shared/service/store.service";
import { PaymentPlan, PlatformType } from "@root/config";
import { TranslateService } from "../../shared/service/translate.service";
import { DateService } from "../../shared/service/date.service";
import { LoginService } from "@root/router/login/login.service";
import { ACLService } from "@delon/acl";

interface IHttpResponse {
  error_code: number;
  error_msg: string;
  data: any;
}

export enum EditionStatus {
  Normal = 1,
  Expired = 2,
  Exceed_In_Limit = 3,
  Exceed_Out_Limit = 4,
}

interface PackageData {
  edition: PaymentPlan; // 当前版本
  // status:         EditionStatus, // 当前版本状态
  quantity: number; // 购买的房间数量
  rest_days: number; // 版本剩余天数
  expire_time: string; // 过期时间
  start_time: string; // 开始时间
}

export const PlanNameMap = {
  [PaymentPlan.Default]: "试用版",
  [PaymentPlan.Standard]: "标准版",
  [PaymentPlan.Professional]: "专业版",
  [PaymentPlan.Enterprise]: "企业版",
};

/** 国内版的版本服务 */
@Injectable({
  providedIn: "root",
})
export class MemberService {
  // 免费版最大房源数量
  /** @deprecated */
  readonly FreeEditionMaxQuantity = 3;

  // 企业版最小房源数量
  readonly EnteEditionMinQuantity = 10;

  notify$ = new Subject();
  packageData: PackageData;
  hasNewComerPromotion = false; // 是否有新手优惠券未领
  gotNewComerPromotion = false; // 是否领取了新手优惠券
  private logout$ = this.loginService.logout$.pipe(
    tap(() => {
      // this.packageData = null;
      // this.status = null;
      [0, 1, 3, 7, 15, 30].forEach((i) => {
        this.storeService.remove(`host_has_notified_will_expire_${i}_days_later`, 2);
      });
    }),
  );
  //#region refactor
  private houseQuantity: number;
  private status: EditionStatus;

  constructor(
    private http: HttpClient,
    private modal: NzModalService,
    private router: Router,
    private storeService: StoreService,
    private t: TranslateService,
    private dateSrv: DateService,
    private loginService: LoginService,
    private acl: ACLService,
  ) {
    // this.editionStatusChange$.subscribe();
    this.logout$.subscribe();
  }

  // 当前版本
  get edition() {
    return this.packageData?.edition;
  }

  //#endregion

  // 当前版本剩余可用房间数量
  get remainingAvailableQuantity() {
    return this.packageData?.quantity - this.houseQuantity;
  }

  get isSubAccount() {
    return this.acl.can({ role: ["slave"] });
    // return _.get(this.systemService, "subAccountInfo.isSubAccount");
  }

  /** @deprecated  */
  isFreeEditionExceedMaxQuantityLimit(quantity = 0) {
    return this.isFreeEdition() && this.houseQuantity + quantity > this.FreeEditionMaxQuantity;
  }

  isEditionExceedQuantityLimit(quantity = 0) {
    return this.isNormalEditionExceedQuantityLimit(quantity);
    // || this.isFreeEditionExceedMaxQuantityLimit(quantity);
  }

  isEditionExpiredOrExceed() {
    return this.isEditionStatusExpired() || this.isEditionStatusExceedOutLimit();
  }

  /** @deprecated  */
  isFreeEdition() {
    return this.edition === PaymentPlan.Standard;
  }

  isDefaultEdition() {
    return this.edition === PaymentPlan.Default;
  }

  isVIPEdition() {
    // return !this.isFreeEdition() &&
    return !this.isDefaultEdition();
  }

  fetchEditionInfo() {
    return this.http.get("/api/member/detail") as Observable<IHttpResponse>;
  }

  requestEditionInfo() {
    this.fetchEditionInfo().subscribe((res: any) => {
      if (res.error_code === 0) {
        const status = res?.data?.status;
        const houseQuantity = res?.data?.house_quantity;
        const current = res?.data?.current;

        this.status = status;
        this.houseQuantity = houseQuantity;
        this.packageData = current;
        this.hasNewComerPromotion = res?.data?.has_newcomer_promotion;
        this.gotNewComerPromotion = res?.data?.got_newcomer_promotion;

        this.notify$.next(null);
        if (res?.data?.next?.length === 0) {
          this.checkIsEditionWillExpire();
        }

        /** 埋点 */
        try {
          window["heap"]?.addUserProperties({ plan_edition: PlanNameMap[this.edition] });
        } catch (e) {}
      }
    });
  }

  isExpiredEditionForbiddenPath(path) {
    return this.isEditionExpiredOrExceed() && this.inExpiredEditionBlockPathList(path);
  }

  /** @deprecated  */
  isFreeEditionForbiddenChannel(channel) {
    return this.isFreeEdition() && this.inFreeEditionBlockChannelList(channel);
  }

  /**
   * @deprecated
   * */
  freeEditionUnsupportedWarning(backToPropertyPage = false) {
    return this.modal.error({
      nzTitle: this.t.t("member.pay.免费版不支持此功能"),
      nzCancelText: this.t.t("word.cancel"),
      nzOkText: this.t.t("word.update"),
      nzClosable: false,
      nzOnCancel: () => (backToPropertyPage ? this.router.navigate(["/app/properties"]) : null),
      nzOnOk: () => {
        this.modal.closeAll();
        this.router.navigate(["/app/pay/upgrade"], { queryParams: { target: 8 } });
      },
    });
  }

  // 最多创建两个子账号
  /** @deprecated  */
  freeEditionCreateTeamAccountLimitWarning() {
    return this.modal.error({
      nzTitle: this.t.t("member.pay.免费版创建子账号限制"),
      nzCancelText: this.t.t("word.cancel"),
      nzOkText: this.t.t("word.update"),
      nzClosable: false,
      nzOnOk: () => {
        this.modal.closeAll();
        this.router.navigate(["/app/pay/upgrade"], { queryParams: { target: 8 } });
      },
    });
  }

  // 限制渠道直连
  /**
   * @deprecated
   * */
  freeEditionLinkChannelLimitWarning() {
    return this.modal.warning({
      nzTitle: "请升级至专业版以直连此渠道",
      nzContent: "您当前的版本不支持直连此渠道，请您至少升级至专业版。",
      // nzCancelText: this.t.t("word.cancel"),
      nzOkText: "知道了",
      nzClosable: false,
      // nzOnOk: () => {
      //   this.modal.closeAll();
      //   this.router.navigate(["/app/pay/upgrade"], { queryParams: { target: 8 } });
      // }
    });
  }

  // isFreeEditionForbiddenPath (path) {
  //   return this.isFreeEdition() && this.inFreeEditionBlockPathList(path);
  // }

  // 最多创建3个房间
  /** @deprecated */
  freeEditionCreateHouseLimitWarning() {
    return this.modal.error({
      nzTitle: this.t.t("member.pay.免费版创建房间限制"),
      nzCancelText: this.t.t("word.cancel"),
      nzOkText: this.t.syncGet("word.update"),
      nzClosable: false,
      nzOnOk: () => {
        this.modal.closeAll();
        this.router.navigate(["/app/pay/upgrade"], { queryParams: { target: 8 } });
      },
    });
  }

  // 创建房间限制提醒
  editionCreateHouseLimitWarning() {
    // if (this.isFreeEditionExceedMaxQuantityLimit()) {
    //   this.freeEditionCreateHouseLimitWarning();
    // } else {
    // }
    if (this.isSubAccount) {
      this.childAccountCreateHouseLimitWarning();
    } else {
      this.mainAccountCreateHouseLimitWarning();
    }
  }

  // private inFreeEditionBlockPathList (path) {
  //   return [
  //     // '/app/shop/list',
  //     // '/app/tasks',
  //   ].indexOf(path) !== -1;
  // }

  editionExpiredOrExceedWarning() {
    console.log("edition expired or exceed warning");

    if (this.isEditionStatusExpired()) {
      this.editionExpiredWarning();
    } else {
      this.editionExceedOutLimitWarning();
    }
  }

  private isEditionStatusNormal() {
    return this.status === EditionStatus.Normal;
    // return edition === EditionStatus.Normal;
  }

  //#region modal

  private isEditionStatusExpired() {
    return this.status === EditionStatus.Expired;
  }

  private isEditionStatusExceedOutLimit() {
    return this.status === EditionStatus.Exceed_Out_Limit;
  }

  private isNormalEditionExceedQuantityLimit(quantity = 0) {
    return this.isEditionStatusNormal() && this.houseQuantity + quantity > this.packageData?.quantity;
  }

  private inExpiredEditionBlockPathList(path) {
    return (
      [
        "/app/inbox",
        "/app/calendar",
        "/app/price",
        "/app/tasks",
        "/app/metrics/transactions",
        "/app/metrics/statistics",
        "/app/shop/list",
      ].indexOf(path) !== -1
    );
  }

  /** @deprecated 前端不再限制，交由后端限制 */
  private inFreeEditionBlockChannelList(channel: PlatformType) {
    return (
      [PlatformType.Fliggy, PlatformType.Ctrip, PlatformType.MeiTuanJiuDian, PlatformType.Douyin].indexOf(channel) !==
      -1
    );
  }

  private checkIsEditionWillExpire() {
    const diff = this.packageData.rest_days;
    /** 只有1，3，7天才通知 */
    if (
      (this.isDefaultEdition() && [0, 1, 3, 7].indexOf(diff) === -1) ||
      // (this.isFreeEdition() && [1, 3, 7].indexOf(diff) === -1) ||
      (this.isVIPEdition() && [0, 1, 3, 7, 15, 30].indexOf(diff) === -1)
    ) {
      return;
    }

    if (!this.isNotifiedForSpecifiedDaysBeforeExpire(diff)) {
      this.editionWillExpireWarning();
      this.markNotifiedForSpecifiedDaysBeforeExpire(diff);
    }
  }

  private mainAccountCreateHouseLimitWarning() {
    this.modal.warning({
      // nzTitle: "您的房间数量已经到达您购买的上限，暂时无法新建房间",
      nzTitle: this.t.t("warning.cannot_create_rooms"),
      nzMaskClosable: false,
      // nzOkText: "升级",
      nzOkText: this.t.t("word.update"),
      nzCancelText: this.t.t("word.cancel"),
      nzOnCancel: () => this.router.navigate(["/app/properties"]),
      nzOnOk: () =>
        this.router.navigate(["/app/pay/upgrade"], {
          queryParams: { target: 4 },
        }),
    });
  }

  private childAccountCreateHouseLimitWarning() {
    this.modal.warning({
      // nzTitle: `您的房间数量已到达购买的上限，暂时无法新建房间，如需创建，请先通知主账号进行升级操作。`,
      nzTitle: this.t.t("warning.subaccount_cannot_create_rooms"),
      nzMaskClosable: false,
      // nzOkText: "知道了",
      nzOkText: this.t.t("word.got_it"),
    });
  }

  private editionExpiredWarning() {
    if (this.isSubAccount) {
      this.childAccountStatusExpiredWarning();
    } else {
      this.mainAccountStatusExpiredWarning();
    }
  }

  /**
   * @description 子账号过期提醒
   * */
  private childAccountStatusExpiredWarning() {
    const expireDate = this.dateSrv.format(this.packageData.expire_time, ["YMD"]);
    this.modal.warning({
      // nzTitle: `您的产品已于${expireDate}到期，请尽快通知主账号续费。`,
      nzTitle: this.t.t("warning.subaccount_edition_expired", { expireDate }),
      nzMaskClosable: false,
      // nzOkText: "知道了",
      nzOkText: this.t.t("word.got_it"),
      nzOnOk: () => this.router.navigate(["/app/properties"]),
    });
  }

  /**
   * @description 主账号过期提醒
   * */
  private mainAccountStatusExpiredWarning() {
    const edition = this.t.t("pay.package.edition_with_suf_" + this.edition);
    const expireDate = this.dateSrv.format(this.packageData?.expire_time, ["YMD"]);
    this.modal.warning({
      // nzTitle: `您的产品已于${expireDate}到期，需要先续费才可继续使用。`,
      nzTitle: this.t.t("warning.edition_expired", { expireDate, edition }),
      nzMaskClosable: false,
      // nzOkText: "续费",
      nzOkText: this.t.t("word.renew"),
      nzCancelText: this.t.t("word.cancel"),
      nzOnCancel: () => this.router.navigate(["/app/properties"]),
      nzOnOk: () =>
        this.router.navigate(["/app/pay/upgrade"], {
          queryParams: { target: 2 },
        }),
    });
  }

  private editionExceedOutLimitWarning() {
    if (this.isSubAccount) {
      this.childAccountStatusExceedOutLimitWarning();
    } else {
      this.mainAccountStatusExceedOutLimitWarning();
    }
  }

  private childAccountStatusExceedOutLimitWarning() {
    const start = this.dateSrv.format(this.packageData.start_time, ["YMD"]);
    this.modal.warning({
      //   nzTitle: `从${moment(this.editionStatus.start_time).format(
      //     "YYYY年MM月DD日"
      //   )}开始，
      // 您的房间数上限为${this.editionStatus.quantity}，目前已超上限。
      // 请通知主账号立即调整房间数量或升级，以恢复使用。`,
      nzTitle: this.t.t("warning.subaccount_too_many_houses", {
        start,
        quantity: this.packageData.quantity,
      }),
      nzMaskClosable: false,
      // nzOkText: "知道了",
      nzOkText: this.t.t("word.got_it"),
      nzOnOk: () => this.router.navigate(["/app/properties"]),
    });
  }

  private mainAccountStatusExceedOutLimitWarning() {
    const start = this.dateSrv.format(this.packageData.start_time, ["YMD"]);
    this.modal.warning({
      //   nzTitle: `从${moment(this.editionStatus.start_time).format(
      //     "YYYY年MM月DD日"
      //   )}开始，
      // 您的房间数上限为${this.editionStatus.quantity}，目前已超上限。
      // 请立即调整房间数量或升级以恢复使用。`,
      nzTitle: this.t.t("warning.too_many_rooms", {
        start,
        quantity: this.packageData.quantity,
      }),
      nzMaskClosable: false,
      // nzOkText: "升级",
      nzOkText: this.t.t("word.update"),
      // nzCancelText: "调整房间",
      nzCancelText: this.t.t("pay.adjust_the_room"),
      nzOnCancel: () => this.router.navigate(["/app/properties"]),
      nzOnOk: () =>
        this.router.navigate(["/app/pay/upgrade"], {
          queryParams: { target: 4 },
        }),
    });
  }

  private editionWillExpireWarning() {
    const diff = this.packageData.rest_days;
    this.modal.warning({
      nzTitle: this.getExpiredModalTitle(diff),
      nzContent: this.getExpiredModalContent(diff),
      nzCancelText: this.t.t("word.cancel"),
      nzOkText:
        // 付费/续费
        this.edition === PaymentPlan.Default ? this.t.t("word.pay") : this.t.t("word.renew"),
      nzOnOk: () =>
        this.edition === PaymentPlan.Default
          ? this.router.navigate(["/app/pay/upgrade"])
          : this.router.navigate(["/app/pay/upgrade"], { queryParams: { target: 2 } }),
    });
  }

  //#endregion

  private isNotifiedForSpecifiedDaysBeforeExpire(day) {
    return this.storeService.get(`host_has_notified_will_expire_${day}_days_later`, 2);
  }

  private markNotifiedForSpecifiedDaysBeforeExpire(day) {
    this.storeService.set(`host_has_notified_will_expire_${day}_days_later`, true, 2);
  }

  private getExpiredModalTitle(restDays) {
    let key;
    if (restDays === 0) {
      key = "warning.will_be_expired_in_today";
    } else if (restDays === 1) {
      key = "warning.will_be_expired_in_day";
    } else {
      key = `warning.will_be_expired_in_days`;
    }
    return this.t.t(key, { diff: restDays });
  }

  private getExpiredModalContent(leftDays) {
    if (this.isDefaultEdition()) {
      const key = `warning.will_be_expired_in_${leftDays === 0 ? "today" : "day"}_info_for_free`;
      return this.t.t(key, { diff: leftDays });
    } else {
      const quantity = this.packageData.quantity;
      let key;
      if (leftDays === 0) {
        key = `warning.will_be_expired_in_today_info_for_room${quantity === 1 ? "" : "s"}`;
      } else if (leftDays === 1) {
        key = `warning.will_be_expired_in_day_info_for_room${quantity === 1 ? "" : "s"}`;
      } else {
        key = `warning.will_be_expired_in_days_info_for_room${quantity === 1 ? "" : "s"}`;
      }
      const edition = this.t.t("pay.package.edition_with_suf_" + this.edition);
      return this.t.t(key, { diff: leftDays, edition, quantity });
    }
  }

  clear() {
    this.packageData = null;
    this.hasNewComerPromotion = false; // 是否有新手优惠券未领
    this.gotNewComerPromotion = false; // 是否领取了新手优惠券
    this.houseQuantity = null;
    this.status = null;
  }
}
